import React, { useEffect, useRef, useState } from "react";
import Header from "./header";
import Footer from "./Footer";


const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const LIMITS = {
    name: 100,
    email: 100,
    company: 100,
    message: 2000,
};

function validate(values) {
    const errors = {};

    if (!values.name.trim()) {
        errors.name = "Please enter your name.";
    } else if (values.name.length > LIMITS.name) {
        errors.name = `Name must be ${LIMITS.name} characters or fewer.`;
    }

    if (!values.email.trim()) {
        errors.email = "Please enter your work email.";
    } else if (!EMAIL_PATTERN.test(values.email.trim())) {
        errors.email = "Please enter a valid email address.";
    } else if (values.email.length > LIMITS.email) {
        errors.email = `Email must be ${LIMITS.email} characters or fewer.`;
    }

    if (values.company.length > LIMITS.company) {
        errors.company = `Company name must be ${LIMITS.company} characters or fewer.`;
    }

    if (!values.message.trim()) {
        errors.message = "Please enter a message.";
    } else if (values.message.length > LIMITS.message) {
        errors.message = `Message must be ${LIMITS.message} characters or fewer.`;
    }

    return errors;
}

const INITIAL_VALUES = { name: "", email: "", company: "", message: "" };

export default function Contact() {
    const [values, setValues] = useState(INITIAL_VALUES);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [note, setNote] = useState(null);

    const heroRef = useRef(null);


    useEffect(() => {
        const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const revealEls = document.querySelectorAll(".contact-page .reveal");

        let io;
        if ("IntersectionObserver" in window && !reduceMotion) {
            io = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            entry.target.classList.add("visible");
                            io.unobserve(entry.target);
                        }
                    });
                },
                { threshold: 0.15, rootMargin: "0px 0px -40px 0px" }
            );
            revealEls.forEach((el) => io.observe(el));
        } else {
            revealEls.forEach((el) => el.classList.add("visible"));
        }

        return () => {
            if (io) io.disconnect();
        };
    }, []);


    useEffect(() => {
        const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const heroSection = heroRef.current;
        const blobs = heroSection ? heroSection.querySelectorAll(":scope > .blob") : [];

        if (!heroSection || !blobs.length || reduceMotion || !window.matchMedia("(pointer: fine)").matches) {
            return;
        }

        const onMouseMove = (e) => {
            const rect = heroSection.getBoundingClientRect();
            const cx = (e.clientX - rect.left) / rect.width - 0.5;
            const cy = (e.clientY - rect.top) / rect.height - 0.5;
            blobs.forEach((blob, i) => {
                const strength = 18 + i * 6;
                blob.style.translate = `${cx * strength}px ${cy * strength}px`;
            });
        };

        heroSection.addEventListener("mousemove", onMouseMove, { passive: true });
        return () => heroSection.removeEventListener("mousemove", onMouseMove);
    }, []);

    function handleChange(e) {
        const { name, value } = e.target;
        const nextValues = { ...values, [name]: value };
        setValues(nextValues);

        if (touched[name]) {
            const nextErrors = validate(nextValues);
            setErrors((prev) => ({ ...prev, [name]: nextErrors[name] }));
        }
    }

    function handleBlur(e) {
        const { name } = e.target;
        setTouched((prev) => ({ ...prev, [name]: true }));
        const nextErrors = validate(values);
        setErrors((prev) => ({ ...prev, [name]: nextErrors[name] }));
    }

    function handleSubmit(e) {
        e.preventDefault();
        const nextErrors = validate(values);
        setErrors(nextErrors);
        setTouched({ name: true, email: true, company: true, message: true });

        if (Object.keys(nextErrors).length > 0) {
            setNote(null);
            return;
        }

        setSubmitting(true);
        setNote(null);

        setTimeout(() => {
            setSubmitting(false);
            setNote({
                text: "This is a design template — the form isn't connected to a server yet.",
                tone: "success",
            });
        }, 700);
    }

    return (
        <>
            <Header />
            <div className="contact-page">
                <section className="contact-hero" ref={heroRef}>
                    <div className="blob blob1" aria-hidden="true"></div>
                    <div className="blob blob2" aria-hidden="true"></div>

                    <div className="contact-hero-inner">
                        <span className="pill page-anim" style={{ "--d": ".05s" }}>
                            <span className="dot">✓</span> Usually reply within a day
                        </span>
                        <h1 className="page-anim" style={{ "--d": ".15s" }}>
                            Let's talk <span className="accent">numbers.</span>
                        </h1>
                        <p className="lede page-anim" style={{ "--d": ".28s" }}>
                            Questions about pricing, a demo, or how Ledgerline fits your close process — send us a
                            note and a real person will get back to you.
                        </p>
                    </div>
                </section>

                <section>
                    <div className="wrap">
                        <div className="contact-grid">
                            <div className="reveal">
                                <span className="section-eyebrow">● Send a message</span>
                                <h2 style={{ fontSize: "clamp(24px,2.8vw,32px)", marginBottom: 28 }}>
                                    Tell us what you need.
                                </h2>

                                <form noValidate onSubmit={handleSubmit}>
                                    <div className="field-row">
                                        <div className={`field${errors.name ? " has-error" : ""}`}>
                                            <label htmlFor="cName">Name</label>
                                            <input
                                                type="text"
                                                id="cName"
                                                name="name"
                                                placeholder="Jordan Marsh"
                                                autoComplete="name"
                                                maxLength={LIMITS.name}
                                                value={values.name}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                aria-invalid={!!errors.name}
                                                aria-describedby={errors.name ? "cName-error" : undefined}
                                            />
                                            {errors.name && (
                                                <span className="field-error" id="cName-error">{errors.name}</span>
                                            )}
                                        </div>
                                        <div className={`field${errors.email ? " has-error" : ""}`}>
                                            <label htmlFor="cEmail">Work email</label>
                                            <input
                                                type="email"
                                                id="cEmail"
                                                name="email"
                                                placeholder="you@company.com"
                                                autoComplete="email"
                                                maxLength={LIMITS.email}
                                                value={values.email}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                aria-invalid={!!errors.email}
                                                aria-describedby={errors.email ? "cEmail-error" : undefined}
                                            />
                                            {errors.email && (
                                                <span className="field-error" id="cEmail-error">{errors.email}</span>
                                            )}
                                        </div>
                                    </div>

                                    <div className={`field${errors.company ? " has-error" : ""}`}>
                                        <label htmlFor="cCompany">
                                            Company <span className="hint">— optional</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="cCompany"
                                            name="company"
                                            placeholder="Northfield Logistics"
                                            autoComplete="organization"
                                            maxLength={LIMITS.company}
                                            value={values.company}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            aria-invalid={!!errors.company}
                                            aria-describedby={errors.company ? "cCompany-error" : undefined}
                                        />
                                        {errors.company && (
                                            <span className="field-error" id="cCompany-error">{errors.company}</span>
                                        )}
                                    </div>

                                    <div className={`field${errors.message ? " has-error" : ""}`}>
                                        <label htmlFor="cMessage">Message</label>
                                        <textarea
                                            id="cMessage"
                                            name="message"
                                            placeholder="What are you hoping to solve?"
                                            maxLength={LIMITS.message}
                                            value={values.message}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            aria-invalid={!!errors.message}
                                            aria-describedby={errors.message ? "cMessage-error" : undefined}
                                        />
                                        {errors.message && (
                                            <span className="field-error" id="cMessage-error">{errors.message}</span>
                                        )}
                                    </div>

                                    <button
                                        type="submit"
                                        className="btn-global btn-primary-global form-submit"
                                        style={{ width: "auto" }}
                                        disabled={submitting}
                                    >
                                        {submitting ? "Sending…" : "Send message"}
                                    </button>

                                    {note && (
                                        <div className={`form-note visible ${note.tone}`}>{note.text}</div>
                                    )}
                                </form>
                            </div>

                            <div className="reveal" style={{ "--d": ".12s" }}>
                                <div className="contact-card">
                                    <div
                                        className="blob blob3"
                                        style={{ top: -60, right: -60, width: 220, height: 220 }}
                                    ></div>
                                    <h3>Other ways to reach us</h3>
                                    <p>Prefer email or want to talk to sales directly? Use whichever's easiest.</p>

                                    <div className="contact-info-row">
                                        <div className="ic">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                                                <path d="M4 4h16v16H4z" opacity="0" />
                                                <path d="M22 6l-10 7L2 6" />
                                                <path d="M2 6h20v12H2z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <div className="label">Email</div>
                                            <div className="val">hello@ledgerline.example</div>
                                        </div>
                                    </div>
                                    <div className="contact-info-row">
                                        <div className="ic">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                                                <path d="M18 10c0 6-6 11-6 11s-6-5-6-11a6 6 0 1112 0z" />
                                                <circle cx="12" cy="10" r="2.2" />
                                            </svg>
                                        </div>
                                        <div>
                                            <div className="label">Office</div>
                                            <div className="val">Placeholder — Bengaluru, IN</div>
                                        </div>
                                    </div>
                                    <div className="contact-info-row">
                                        <div className="ic">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                                                <circle cx="12" cy="12" r="9" />
                                                <path d="M12 7v5l3 3" />
                                            </svg>
                                        </div>
                                        <div>
                                            <div className="label">Response time</div>
                                            <div className="val">Within 1 business day</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <div className="cta-band">
                    <div className="wrap">
                        <div>
                            <h2>Rather see it live?</h2>
                            <p>Book a 20-minute walkthrough with our team — no pressure, no pitch deck.</p>
                        </div>
                        <button href="/" className="btn-global btn-dark-global btn-lg-global">Register</button>
                    </div>
                </div>
            </div>

            <Footer />
        </>
    );
}